import * as fromSeriousSystem from '@serious-system';

/**
 * List of assistant icons.
 */
export const ASSISTANT_ICONS = [
  'email-filled',
  'convention-filled',
  'summary-filled',
  'note-filled',
  'voice-filled',
  'translate-filled',
  'text-filled',
  'daily-recap-filled',
  'presentation-filled',
  'prompt-filled',
  'squadbox-guide-filled',
  'idea-filled',
  'feedback-filled',
  'arcelor-legal-filled',
  'arcelor-politique-travail-filled',
  'arcelor-comparaison-contrat-filled',
];

/**
 * List of custom assistant icons
 */
export const ASSISTANT_CUSTOM_ICONS = [
  'default',
  'question',
  'comment',
  'info',
  'document',
  'file-check',
  'file-edit',
  'list',
  'bill',
  'folder',
  'briefcase',
  'search',
  'user-time',
  'user-check',
  'handshake',
  'bulb',
  'clock',
  'calendar-edit',
  'shield',
  'law',
  'monitor',
  'trend',
  'certificate',
  'target',
];

/**
 * List of AI icons
 */
export const ASSISTANT_AI_ICONS = ['stars'];

/**
 * Determines the icon type based on the provided path
 * @param logoPath The icon path to check
 * @returns 'assistant', 'custom' or 'image'
 */
export function getLogoType(
  logoPath: string,
): 'assistant' | 'custom' | 'ai' | 'image' {
  if (ASSISTANT_ICONS.includes(logoPath)) {
    return 'assistant';
  }

  if (ASSISTANT_CUSTOM_ICONS.includes(logoPath)) {
    return 'custom';
  }

  if (ASSISTANT_AI_ICONS.includes(logoPath)) {
    return 'ai';
  }

  return 'image';
}

/**
 * Converts an icon path to AssistantIconsType
 * @param logoPath The icon path to convert
 * @returns The corresponding AssistantIconsType
 */
export function getAssistantIcon(
  logoPath: string,
): fromSeriousSystem.AssistantIconsType {
  return logoPath as unknown as fromSeriousSystem.AssistantIconsType;
}

/**
 * Converts an icon path to AssistantCustomIconsType
 * @param logoPath The icon path to convert
 * @returns The corresponding AssistantCustomIconsType
 */
export function getAssistantCustomIcon(
  logoPath: string,
): fromSeriousSystem.AssistantCustomIconsType {
  return logoPath as unknown as fromSeriousSystem.AssistantCustomIconsType;
}

/**
 * Converts an icon path to AiIconsType
 * @param logoPath The icon path to convert
 * @returns The corresponding AiIconsType
 */
export function getAssistantAiIcon(
  logoPath: string,
): fromSeriousSystem.AiIconsType {
  return logoPath as unknown as fromSeriousSystem.AiIconsType;
}

/**
 * Maps a skill/assistant logo identifier to a Serious System icon descriptor for
 * `[sdUseIcon]` (sprite-based). Returns null for image-based logos, which the
 * sprite-driven icon directive can't render — callers should fall back to a
 * logo-less row in that case.
 */
export function getAssistantIconDescriptor(
  logoPath: string | null | undefined,
): fromSeriousSystem.IconsType | null {
  if (!logoPath) {
    return null;
  }
  switch (getLogoType(logoPath)) {
    case 'assistant':
      return { type: 'assistant', icon: getAssistantIcon(logoPath) };
    case 'custom':
      return {
        type: 'assistantCustom',
        icon: getAssistantCustomIcon(logoPath),
      };
    case 'ai':
      return { type: 'ai', icon: getAssistantAiIcon(logoPath) };
    default:
      return null;
  }
}
